import Image from 'next/image';
import { MouseEvent, memo, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { BuildingOfficeIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { Badge, Heading, IconButton, SkeletonLoading } from '@restworld/ui-ds-base';
import { cn, customImageLoader, useResponsiveBreakPoints } from '@restworld/utils-common';
import { GoogleRatingCard } from '../../../legacy';

import BlurImage from '../../../utility-components/blur-image';

export interface ExplorerMapPopupCardProps {
  bgImage?: string;
  jpTitle?: string;
  companyTitle: string;
  otherInfos?: string[];
  onCardClose: (e: MouseEvent<HTMLAnchorElement>) => void;
  companyLocation?: string;
  restaurantLogo?: string;
  loading?: boolean;
  showRestaurantDetails?: boolean;
  isRestaurantsExplorer?: boolean;
  googleMapsInfo?: {
    rating?: number;
    reviews?: number;
  };
  isJobPositionOutsideItaly?: boolean;
}
export const ExplorerMapPopupCard = memo(
  ({
    bgImage,
    companyTitle,
    jpTitle,
    otherInfos,
    onCardClose,
    companyLocation,
    restaurantLogo,
    loading,
    showRestaurantDetails,
    isRestaurantsExplorer,
    googleMapsInfo,
    isJobPositionOutsideItaly,
  }: ExplorerMapPopupCardProps) => {
    const { t } = useTranslation();
    const { isDesktop } = useResponsiveBreakPoints();
    const googleEle = useMemo(
      () =>
        isRestaurantsExplorer && googleMapsInfo?.rating ? (
          <GoogleRatingCard
            googleLogoSize={{
              height: 24,
              width: 72,
            }}
            rating={googleMapsInfo.rating}
            backgroundColor="white"
            description={isDesktop ? `${googleMapsInfo.reviews} ${t('common:reviews')}` : ''}
            className="rounded-none p-2 mx-0 w-full flex flex-row gap-4 bg-transparent"
            ratingIconSize="xs"
            ratingLabelSize={isDesktop ? 'sm' : 'xs'}
            showGoogleIcon={isDesktop}
          />
        ) : null,
      [t, googleMapsInfo?.rating, googleMapsInfo?.reviews, isDesktop, isRestaurantsExplorer]
    );
    const companyLocationEle = useMemo(
      () =>
        isRestaurantsExplorer ? (
          <span className="flex flex-row items-center gap-1.5 cursor-pointer">
            <MapPinIcon className="h-5 text-gray-500" />
            <Heading size={6} color="muted" weight="normal">
              <span className="line-clamp-2">{companyLocation}</span>
            </Heading>
          </span>
        ) : companyLocation ? (
          <div className="hidden md:block">
            <span className="flex flex-row items-center gap-1.5 cursor-pointer">
              {isJobPositionOutsideItaly ? '🌍' : <MapPinIcon className="h-5 text-gray-500" />}
              <Heading size={6} color="muted" weight="normal">
                <span className="line-clamp-2">{companyLocation}</span>
              </Heading>
            </span>
          </div>
        ) : null,
      [companyLocation, isJobPositionOutsideItaly, isRestaurantsExplorer]
    );
    const otherInfosEle = useMemo(
      () =>
        otherInfos?.length ? (
          <div className="flex gap-2 flex-wrap">
            {otherInfos.map((v) => (
              <Badge key={v} variant="fresh">
                {v}
              </Badge>
            ))}
          </div>
        ) : null,
      [otherInfos]
    );
    const companyDescEle = useMemo(
      () => <label className="text-base line-clamp-2 text-black font-semibold cursor-pointer">{jpTitle}</label>,
      [jpTitle]
    );
    const crossMarkEl = useMemo(
      () => (
        <StyledCrossMarkWrapper position={!bgImage ? 'alignRight' : isDesktop ? 'alignRight' : 'alignLeft'}>
          <IconButton
            Icon={XMarkIcon}
            ariaLabel="close"
            variant="restworld"
            className={'bg-purple-50 rounded-md text-purple-700 hover:bg-purple-50 active:bg-purple-50'}
            onMouseDown={(e) => {
              e.stopPropagation();
              onCardClose(e as unknown as MouseEvent<HTMLAnchorElement>);
            }}
            onClick={(e) => {
              // also stop propagation on click to be safe
              e.stopPropagation();
            }}
          />
        </StyledCrossMarkWrapper>
      ),
      [bgImage, isDesktop, onCardClose]
    );
    const renderComponent = useMemo(() => {
      if (loading)
        return (
          <div className="relative max-w-xs w-full md:w-44 h-full rounded-2xl bg-white">
            <SkeletonLoading animation variant="map-details-card" />
          </div>
        );
      if (showRestaurantDetails || isRestaurantsExplorer) {
        return (
          <>
            <MobileCardWrapper bgImage={bgImage}>
              {bgImage ? (
                <BlurImage
                  src={bgImage}
                  className={'rounded-2xl h-[12.5rem] object-cover'}
                  alt="background-color"
                  dimensions={{
                    width: 144,
                    height: 160,
                  }}
                />
              ) : null}
              {bgImage ? crossMarkEl : null}
              <CardWrapper bgImage={bgImage}>
                <CardBody bgImage={bgImage}>
                  {!bgImage ? crossMarkEl : null}
                  {isRestaurantsExplorer ? (
                    <Heading className="cursor-pointer leading-5.25">{companyTitle}</Heading>
                  ) : (
                    <div>
                      <span className="flex flex-row items-center gap-1.5 cursor-pointer">
                        <BuildingOfficeIcon className="h-5 text-gray-500" />
                        <Heading color="muted">{companyTitle}</Heading>
                      </span>
                    </div>
                  )}
                  {companyDescEle}
                  {companyLocationEle}
                  {otherInfosEle}
                  {googleEle}
                </CardBody>
              </CardWrapper>
            </MobileCardWrapper>
            <DesktopCardWrapper bgImage={bgImage}>
              <DesktopBackgroundWrapper>
                {bgImage ? (
                  <BlurImage
                    src={bgImage}
                    className={'max-h-40 bg-no-repeat bg-cover w-full rounded-t-2xl hidden md:block object-cover'}
                    alt="background-color"
                    dimensions={{
                      width: 144,
                      height: 160,
                    }}
                  />
                ) : null}
                {bgImage ? crossMarkEl : null}
                {restaurantLogo ? (
                  <Image
                    src={restaurantLogo}
                    alt="logo"
                    className="absolute border border-gray-200 rounded bottom-2 left-2 shadow bg-white"
                    width={56}
                    height={56}
                    loader={customImageLoader}
                  />
                ) : null}
              </DesktopBackgroundWrapper>
              <CardBody bgImage={bgImage}>
                {!bgImage ? crossMarkEl : null}
                {isRestaurantsExplorer ? (
                  <Heading className="cursor-pointer leading-5.25">{companyTitle}</Heading>
                ) : null}
                {companyDescEle}
                {companyLocationEle}
                {otherInfosEle}
                {googleEle}
              </CardBody>
            </DesktopCardWrapper>
          </>
        );
      }
      return (
        <div className="bg-white rounded-2xl flex flex-col gap-2 p-4 cursor-pointer">
          {crossMarkEl}
          {companyDescEle}
          {companyLocation ? (
            <span className="flex flex-row items-center gap-1.5 cursor-pointer">
              {isJobPositionOutsideItaly ? '🌍' : <MapPinIcon className="h-5 text-gray-500" />}
              <Heading size={6} color="muted" weight="normal">
                <span className="line-clamp-2">{companyLocation}</span>
              </Heading>
            </span>
          ) : null}
          {otherInfosEle}
        </div>
      );
    }, [
      bgImage,
      companyDescEle,
      companyLocation,
      companyLocationEle,
      companyTitle,
      crossMarkEl,
      googleEle,
      isJobPositionOutsideItaly,
      isRestaurantsExplorer,
      loading,
      otherInfosEle,
      restaurantLogo,
      showRestaurantDetails,
    ]);
    return <Card>{renderComponent}</Card>;
  }
);

export default ExplorerMapPopupCard;

const Card = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('relative w-full cursor-pointer h-full min-w-[23rem] min-h-[7.5rem]', className)} {...props} />
);

const CardBody = ({
  bgImage,
  className,
  ...props
}: {
  bgImage?: string;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col gap-2 p-4 h-full bg-white md:relative md:w-full md:h-fit',
      !bgImage ? 'rounded-2xl md:rounded-2xl' : 'rounded-r-2xl md:rounded-b-2xl md:rounded-tr-none',
      className
    )}
    {...props}
  />
);

const CardWrapper = ({
  bgImage,
  className,
  ...props
}: {
  bgImage?: string;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('absolute right-0 top-0 h-full w-[65%]', bgImage ? 'w-[65%]' : 'w-full', className)} {...props} />
);

const StyledCrossMarkWrapper = ({
  position,
  className,
  ...props
}: {
  position?: 'alignLeft' | 'alignRight' | 'justifyRight';
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'w-fit z-10',
      position
        ? {
            alignLeft: 'absolute top-2 right-0 left-2 mr-auto',
            alignRight: 'absolute top-2 right-2 left-0 ml-auto',
            justifyRight: 'flex justify-end ml-auto w-full',
          }[position]
        : '',
      className
    )}
    {...props}
  />
);

const DesktopCardWrapper = ({
  bgImage,
  className,
  ...props
}: {
  bgImage?: string;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'hidden md:block relative w-full shadow-lg border-gray-200 rounded-none rounded-t-2xl',
      !bgImage ? 'h-fit' : 'h-40',
      className
    )}
    {...props}
  />
);

const DesktopBackgroundWrapper = ({
  bgImage,
  className,
  ...props
}: {
  bgImage?: string;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'cursor-pointer relative rounded-t-2xl border-white',
      !bgImage ? 'border-none' : 'border-t',
      className
    )}
    {...props}
  />
);

const MobileCardWrapper = ({
  bgImage,
  className,
  ...props
}: {
  bgImage?: string;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('md:hidden relative max-w-lg min-h-[12.5rem]', !bgImage ? 'min-h-[8rem]' : '', className)}
    {...props}
  />
);
